import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Mic, Send, Copy, Loader2, Square, Paperclip, FileText, GitBranch, RefreshCw, Eye, Library } from "lucide-react";
import { api } from "@/api/client";
import VaultPicker from "@/components/chat/VaultPicker";

/* Renders a sent attachment: image thumbnail (auth blob fetch) or a file chip. */
function Attachment({ att }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const isImage = (att.mime_type || "").startsWith("image/");
  const path = (att.url || "").replace(/^\/api/, "");
  useEffect(() => {
    let revoked = null;
    if (isImage && path) {
      api.get(path, { responseType: "blob" }).then((r) => {
        const u = URL.createObjectURL(r.data);
        revoked = u;
        setBlobUrl(u);
      }).catch(() => {});
    }
    return () => revoked && URL.revokeObjectURL(revoked);
  }, [path, isImage]);

  const open = () => api.get(path, { responseType: "blob" }).then((r) => window.open(URL.createObjectURL(r.data), "_blank")).catch(() => {});

  if (isImage) {
    return (
      <button onClick={open} data-testid="jim-attach-img" className="block border-2 border-black rounded-none overflow-hidden bg-white shadow-[3px_3px_0px_#000] mb-1.5">
        {blobUrl ? <img src={blobUrl} alt={att.name} className="max-h-40 w-auto object-cover" /> : <div className="h-24 w-32 grid place-items-center"><Loader2 size={16} className="animate-spin text-black/40" /></div>}
      </button>
    );
  }
  return (
    <button onClick={open} data-testid="jim-attach-file" className="flex items-center gap-2 border-2 border-black bg-white text-black rounded-none px-2.5 py-1.5 shadow-[3px_3px_0px_#000] mb-1.5 max-w-full">
      <FileText size={15} className="shrink-0" />
      <span className="font-mono text-[10px] truncate">{att.name}</span>
    </button>
  );
}

/* JIM — full-screen system-companion terminal. X close, Whisper mic capture,
   copy-able message blocks, sticky footer input. Galaxy S20 chassis locked. */
export default function JimTerminal({ onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "JIM online. Tell me what to build, fix, or explain — I keep it simple for you and precise for the agents." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [attach, setAttach] = useState(null); // { file, name, size, previewUrl, isImage }
  const [vaultPicks, setVaultPicks] = useState([]);
  const [showVault, setShowVault] = useState(false);
  const [branch, setBranch] = useState("MAIN");
  const [branchOpen, setBranchOpen] = useState(false);
  const [lastSync, setLastSync] = useState(Date.now());
  const [now, setNow] = useState(Date.now());
  const [syncing, setSyncing] = useState(false);
  const [visionActive, setVisionActive] = useState(false);
  const fileRef = useRef(null);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  // Chat surfaces suppress the floating "+" FAB and the founder error badge.
  useEffect(() => {
    window.__mistChatSurface = true;
    window.dispatchEvent(new CustomEvent("mist:chat-surface", { detail: { active: true } }));
    return () => {
      window.__mistChatSurface = false;
      window.dispatchEvent(new CustomEvent("mist:chat-surface", { detail: { active: false } }));
    };
  }, []);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf,.txt,.heic,.mp4,.mov,.webm,.m4v,.mp3,.wav,.m4a,.aac,.ogg,.flac";
  const ALLOWED = ["jpg", "jpeg", "png", "webp", "pdf", "txt", "heic",
    "mp4", "mov", "webm", "m4v", "mp3", "wav", "m4a", "aac", "ogg", "flac"];
  const VIDEO_EXT = ["mp4", "mov", "webm", "m4v"];
  const AUDIO_EXT = ["mp3", "wav", "m4a", "aac", "ogg", "flac"];
  const limitFor = (ext) => VIDEO_EXT.includes(ext) ? 50 * 1024 * 1024 : AUDIO_EXT.includes(ext) ? 24 * 1024 * 1024 : 10 * 1024 * 1024;

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED.includes(ext)) { toast.error("Unsupported file type"); return; }
    if (f.size > limitFor(ext)) { toast.error(`File too large (max ${Math.round(limitFor(ext) / (1024 * 1024))} MB)`); return; }
    const isImage = f.type.startsWith("image/");
    setAttach({ file: f, name: f.name, size: f.size, isImage, previewUrl: isImage ? URL.createObjectURL(f) : null });
  };

  const removeAttach = () => {
    if (attach?.previewUrl) URL.revokeObjectURL(attach.previewUrl);
    setAttach(null);
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const syncAgoLabel = () => {
    const mins = Math.floor((now - lastSync) / 60000);
    if (mins < 1) return "JUST NOW";
    if (mins < 60) return `${mins}M AGO`;
    return `${Math.floor(mins / 60)}H AGO`;
  };

  const syncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await api.get("/jim/access"); // re-ping the persistent handshake / repo cache
      setLastSync(Date.now());
      setNow(Date.now());
      toast.success("Repository cache synced");
    } catch { toast.error("Sync failed"); }
    finally { setSyncing(false); }
  };

  const BRANCHES = ["MAIN", "DEV", "STAGING"];

  const send = async () => {
    const text = input.trim();
    if ((!text && !attach && vaultPicks.length === 0) || sending) return;
    setSending(true);
    let uploaded = null;
    try {
      if (attach) {
        const fd = new FormData();
        fd.append("file", attach.file, attach.name);
        const { data } = await api.post("/jim/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded = data;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      setSending(false);
      return;
    }
    const atts = [
      ...vaultPicks.map((p) => ({ asset_id: p.asset_id, kind: p.kind, name: p.name, url: p.url, mime_type: p.kind === "image" ? "image/*" : "" })),
      ...(uploaded ? [uploaded] : []),
    ];
    const hadImage = atts.some((a) => (a.mime_type || "").startsWith("image/") || a.kind === "image");
    setInput("");
    removeAttach();
    setVaultPicks([]);
    setVisionActive(hadImage);
    setMessages((m) => [...m, { role: "user", text, attachments: atts }]);
    try {
      const { data } = await api.post("/jim/chat", { message: text, session_id: sessionRef.current, attachments: atts, branch });
      sessionRef.current = data.session_id;
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "JIM is unavailable right now.";
      setMessages((m) => [...m, { role: "assistant", text: msg }]);
    } finally {
      setSending(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  const [dispatchingKey, setDispatchingKey] = useState(null);
  const sendToAgent = async (snippet, key) => {
    if (dispatchingKey) return;
    setDispatchingKey(key);
    try {
      const { data } = await api.post("/jim/dispatch", { snippet, branch, session_id: sessionRef.current });
      toast.success(`Sent to agent queue · ${data.branch} (#${data.queued_total})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch failed");
    } finally {
      setDispatchingKey(null);
    }
  };

  // Split a message into plain-text and fenced ``` code ``` segments.
  const parseSegments = (text) => {
    const out = [];
    const re = /```(?:[a-zA-Z0-9_-]+)?\n?([\s\S]*?)```/g;
    let last = 0, m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) out.push({ type: "text", value: text.slice(last, m.index) });
      out.push({ type: "code", value: m[1].replace(/\n$/, "") });
      last = re.lastIndex;
    }
    if (last < text.length) out.push({ type: "text", value: text.slice(last) });
    return out.length ? out : [{ type: "text", value: text }];
  };

  const renderJim = (text, i) => {
    const segs = parseSegments(text);
    return (
      <div key={i} className="w-full border-b-2 border-black px-3 py-3" data-testid={`jim-msg-jim-${i}`}>
        <p className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-black mb-1.5">JIM</p>
        <div className="space-y-2">
          {segs.map((s, j) => (
            s.type === "text" ? (
              s.value.trim() && <p key={j} className="font-sans text-[13px] leading-relaxed text-black whitespace-pre-wrap break-words">{s.value.trim()}</p>
            ) : (
              <div key={j} data-testid={`jim-codeblock-${i}-${j}`}>
                <div className="relative">
                  <pre className="border-2 border-black bg-[#0B0B0B] text-green-400 font-mono text-[11px] leading-snug rounded-none pt-3 pb-9 px-3 overflow-x-auto no-scrollbar whitespace-pre">{s.value}</pre>
                  <button onClick={() => copy(s.value)} data-testid={`jim-copy-block-${i}-${j}`}
                    className="absolute bottom-1.5 right-1.5 flex items-center gap-1 border-2 border-black bg-[#4D96FF] text-white font-mono text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-none shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
                    <Copy size={9} strokeWidth={3} /> Copy Block
                  </button>
                </div>
                {s.value.includes("\n") && (
                  <button onClick={() => sendToAgent(s.value, `${i}-${j}`)} disabled={dispatchingKey === `${i}-${j}`} data-testid={`jim-send-agent-${i}-${j}`}
                    className="mt-1.5 w-full flex items-center justify-center gap-1.5 border-2 border-black bg-[#6BCB77] text-black font-mono text-[9px] font-black uppercase tracking-[0.22em] py-2 rounded-none shadow-[3px_3px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-50">
                    {dispatchingKey === `${i}-${j}` ? <Loader2 size={11} className="animate-spin" /> : <GitBranch size={11} strokeWidth={3} />} [ SEND TO AGENT ]
                  </button>
                )}
              </div>
            )
          ))}
        </div>
      </div>
    );
  };

  const renderUser = (m, i) => (
    <div key={i} className="w-full border-b-2 border-black px-3 py-3" data-testid={`jim-msg-user-${i}`}>
      <p className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-[#4D96FF] mb-1.5">You</p>
      {(m.attachments || []).map((a, k) => <Attachment key={k} att={a} />)}
      {m.text && <p className="font-sans text-[13px] leading-relaxed text-black whitespace-pre-wrap break-words">{m.text}</p>}
    </div>
  );

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "jim_voice.webm");
          const { data } = await api.post("/vault-security/blueprint/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data?.transcript) setInput((v) => (v ? v + " " : "") + data.transcript);
          else toast.error("No speech detected");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Transcription unavailable");
        } finally {
          setTranscribing(false);
        }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const stopRec = () => {
    recRef.current?.stop();
    setRecording(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-center bg-black/40" data-testid="jim-terminal">
      <div className="w-full max-w-[412px] h-full flex flex-col bg-white overflow-hidden">
        {/* High-density system header bar */}
        <header className="shrink-0 flex items-center justify-between w-full px-3 py-2.5 bg-white border-b-2 border-black">
          <div className="flex items-center gap-2">
            <button onClick={onClose} data-testid="jim-close-btn" aria-label="Close" className="w-10 h-10 grid place-items-center border-2 border-black bg-white shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><X size={18} strokeWidth={2.6} /></button>
            <button onClick={recording ? stopRec : startRec} disabled={transcribing} data-testid="jim-mic-btn" aria-label="Voice" title="Voice — OpenAI Whisper" className={`w-10 h-10 grid place-items-center border-2 border-black shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none ${recording ? "bg-red-500 text-white animate-pulse" : "bg-[#4D96FF] text-white"}`}>
              {transcribing ? <Loader2 size={17} className="animate-spin" /> : recording ? <Square size={16} strokeWidth={3} /> : <Mic size={17} strokeWidth={2.4} />}
            </button>
          </div>
          <span className="font-mono text-xl font-black tracking-[0.3em] text-black" data-testid="jim-title">J I M</span>
        </header>

        {/* Compact operation panel — repo/branch/sync/vision control board */}
        <div className="shrink-0 bg-white border-b-2 border-black px-3 py-2 space-y-1.5" data-testid="jim-ops-panel">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1 font-mono text-[9px] font-black uppercase tracking-wider text-black min-w-0" data-testid="jim-repo-status">
              <span className="w-2 h-2 bg-green-500 border-2 border-black shrink-0" />
              REPO: CONNECTED <span className="text-black/50 truncate max-w-[110px]">[misteck_preview]</span>
            </span>
            <span data-testid="jim-vision-badge"
              className={`flex items-center gap-1 font-mono text-[8px] font-black uppercase tracking-wider border-2 border-black px-1.5 py-0.5 rounded-none shrink-0 ${
                (attach?.isImage || visionActive) ? "bg-[#4D96FF] text-white" : "bg-white text-black/40"}`}>
              <Eye size={10} strokeWidth={3} /> VISION · {attach?.isImage ? "READY" : visionActive ? "ACTIVE" : "IDLE"}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="relative">
              <button onClick={() => setBranchOpen((o) => !o)} data-testid="jim-branch-btn"
                className="flex items-center gap-1 border-2 border-black bg-white text-black font-mono text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-none shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
                <GitBranch size={11} strokeWidth={3} /> BRANCH: {branch}
              </button>
              {branchOpen && (
                <div className="absolute z-[80] mt-1 left-0 w-32 border-2 border-black bg-white shadow-[3px_3px_0px_#000]" data-testid="jim-branch-menu">
                  {BRANCHES.map((b) => (
                    <button key={b} onClick={() => { setBranch(b); setBranchOpen(false); }} data-testid={`jim-branch-${b.toLowerCase()}`}
                      className={`w-full text-left font-mono text-[9px] font-black uppercase tracking-wider px-2 py-1.5 border-b border-black/10 ${branch === b ? "bg-black text-[#4D96FF]" : "text-black hover:bg-[#F4F4F4]"}`}>
                      {b}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={syncNow} disabled={syncing} data-testid="jim-sync-btn"
              className="flex items-center gap-1 border-2 border-black bg-[#FFDE4D] text-black font-mono text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded-none shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-50">
              <RefreshCw size={11} strokeWidth={3} className={syncing ? "animate-spin" : ""} /> SYNCED {syncAgoLabel()}
            </button>
          </div>
        </div>

        {/* Conversational stream — full-width stacked blocks, hard frame separators */}
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto no-scrollbar bg-white" data-testid="jim-stream">
          {messages.map((m, i) => (
            m.role === "user" ? renderUser(m, i) : renderJim(m.text, i)
          ))}
          {sending && (
            <div className="flex items-center gap-2 px-3 py-3 border-b-2 border-black text-black/60 font-mono text-[11px] uppercase tracking-wider" data-testid="jim-typing"><Loader2 size={13} className="animate-spin" /> JIM is thinking…</div>
          )}
        </div>

        {/* Footer entry tray — hard-edged rectangular field */}
        <footer className="shrink-0 bg-white border-t-2 border-black">
          {attach && (
            <div className="flex items-center gap-2 px-3 pt-2" data-testid="jim-attach-chip">
              <div className="flex items-center gap-2 border-2 border-black bg-white rounded-none px-2 py-1 shadow-[3px_3px_0px_#000] max-w-[85%]">
                {attach.isImage && attach.previewUrl
                  ? <img src={attach.previewUrl} alt="preview" className="w-8 h-8 object-cover border-2 border-black" />
                  : <FileText size={16} className="text-black shrink-0" />}
                <div className="min-w-0">
                  <p className="font-mono text-[10px] font-bold text-black truncate">{attach.name}</p>
                  <p className="font-mono text-[8px] text-black/50">{(attach.size / 1024).toFixed(0)} KB</p>
                </div>
                <button onClick={removeAttach} data-testid="jim-attach-remove" aria-label="Remove" className="ml-1 w-5 h-5 grid place-items-center border-2 border-black bg-red-500 text-white"><X size={11} strokeWidth={3} /></button>
              </div>
            </div>
          )}
          {vaultPicks.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-3 pt-2" data-testid="jim-vault-chips">
              {vaultPicks.map((p) => (
                <span key={p.asset_id} className="flex items-center gap-1 border-2 border-black bg-[#FFDE4D] rounded-none px-2 py-1 shadow-[2px_2px_0px_#000]">
                  <span className="font-mono text-[9px] font-bold uppercase text-black truncate max-w-[90px]">{p.kind}·{p.name}</span>
                  <button onClick={() => setVaultPicks((v) => v.filter((x) => x.asset_id !== p.asset_id))} aria-label="Remove" className="text-black active:scale-90"><X size={11} strokeWidth={3} /></button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-stretch gap-2 px-3 py-3">
            <input ref={fileRef} type="file" accept={ACCEPT} onChange={pickFile} className="hidden" data-testid="jim-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={sending} data-testid="jim-attach-btn" aria-label="Attach file" className="shrink-0 w-11 grid place-items-center border-2 border-black bg-[#FFDE4D] text-black rounded-none shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
              <Paperclip size={18} strokeWidth={2.4} />
            </button>
            <button onClick={() => setShowVault(true)} disabled={sending} data-testid="jim-vault-btn" aria-label="Attach from Vault" className="shrink-0 w-11 grid place-items-center border-2 border-black bg-white text-black rounded-none shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
              <Library size={18} strokeWidth={2.4} />
            </button>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Message JIM…"
              data-testid="jim-input"
              className="flex-1 min-w-0 rounded-none border-2 border-black bg-white font-sans text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#4D96FF]"
            />
            <button onClick={send} disabled={sending || (!input.trim() && !attach && vaultPicks.length === 0)} data-testid="jim-send-btn" className="shrink-0 flex items-center gap-1 rounded-none border-2 border-black bg-[#6BCB77] text-black font-mono text-[11px] font-black uppercase tracking-widest px-3.5 shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={2.6} />} Send
            </button>
          </div>
        </footer>
        {showVault && (
          <VaultPicker endpoint="/jim/context/vault" onClose={() => setShowVault(false)}
            onPick={(picks) => setVaultPicks((v) => {
              const map = {}; [...v, ...picks].forEach((p) => { map[p.asset_id] = p; }); return Object.values(map);
            })} />
        )}
      </div>
    </div>
  );
}
